/**
 * Two films on /why-bb/story that walk through the whole app (Gordon
 * 2026-11-03: "an animation film for the story part … the design and all the
 * features … show the new crate search … one a little more stiff and
 * explanatory, and one more poetic and arty and energetic — slow, fast, fast,
 * slow, slow, fast").
 *
 * Pictures are REAL captures of the live site as a visitor (dark, English):
 * `scripts/tour-film/capture.mjs` → `encode.mjs` → public/images/tour and
 * public/videos/tour. Pages that need an account are shown with the drawn
 * tools from the event-screen films (mocks.tsx). Every word is
 * `tourFilms.<film>.<key>` in messages/*.json. Only claim what BB does today.
 */

import type { MockId } from "@/components/screens/mocks";
import type { LinesFor } from "@/components/screens/films";

export type ShotId =
  | "home-intro" | "home" | "home-more" | "menu" | "crate" | "player" | "release" | "release-credits"
  | "track" | "track-lyrics" | "artist" | "market" | "book" | "sync" | "videos" | "news"
  | "labels" | "demos" | "search" | "academy" | "tuner" | "epx" | "why-calc" | "platform" | "angels"
  | "p-home" | "p-menu" | "p-crate" | "p-player" | "p-release" | "p-track" | "p-market" | "p-artist";

/** Shots that are a short screen recording instead of a still. */
export const SHOT_VIDEOS: Partial<Record<ShotId, string>> = {
  crate: "/videos/tour/crate.mp4",
  "p-crate": "/videos/tour/p-crate.mp4",
};
export const shotSrc = (id: ShotId) => `/images/tour/${id}.webp`;
/** The address shown in the browser frame. */
export const SHOT_PATH: Partial<Record<ShotId, string>> = {
  "home-intro": "/home", home: "/home", "home-more": "/home", menu: "/home", crate: "/home", player: "/home",
  release: "/music/extreme-paranoia-in-stocktown", "release-credits": "/music/i-love-happy-alright-me-too",
  track: "/music/…/love-song", "track-lyrics": "/music/…/love-song", artist: "/artists/happy-alright",
  market: "/marketplace", book: "/book", sync: "/synchronization", videos: "/videos", news: "/news",
  labels: "/labels", demos: "/demos", search: "/search?q=funk", academy: "/academy",
  tuner: "/studio/tuner", epx: "/studio/epx", "why-calc": "/why-bb", platform: "/platform", angels: "/supporters",
};

/* ------------------------------------------------------------------ */
/*  The tour: one page per beat, explained                             */
/* ------------------------------------------------------------------ */

export interface TourBeat {
  ms: number;
  /** Headline, sentence and pill keys in `tourFilms.tour`. */
  h: string;
  body?: string;
  pills?: string[];
  /** A desktop capture (or several, side by side). */
  shot?: ShotId | ShotId[];
  /** The same page on a phone, beside it. */
  phone?: ShotId;
  /** Where the slow camera move goes (CSS transform-origin). */
  focus?: string;
  /** A tool that needs an account: drawn instead of captured. */
  mock?: MockId;
  lines?: LinesFor;
  /** Words only, over BB's cover wall. */
  cover?: boolean;
}

const b = (h: string, rest: Omit<TourBeat, "h" | "ms"> & { ms?: number } = {}): TourBeat => ({
  ms: 7600, body: `${h}Body`, ...rest, h,
});

export const TOUR: TourBeat[] = [
  { ms: 5200, h: "intro", body: "introBody", cover: true },
  b("home", { shot: "home-intro", phone: "p-home", pills: ["homeP1", "homeP2", "homeP3"], focus: "30% 40%" }),
  b("sections", { shot: "home", pills: ["sectionsP1", "sectionsP2", "sectionsP3"], focus: "50% 20%" }),
  b("menu", { shot: "menu", phone: "p-menu", pills: ["menuP1", "menuP2", "menuP3"], focus: "85% 30%" }),
  b("crate", { ms: 12000, shot: "crate", phone: "p-crate", pills: ["crateP1", "crateP2", "crateP3", "crateP4"] }),
  b("player", { shot: "player", phone: "p-player", pills: ["playerP1", "playerP2", "playerP3"], focus: "50% 90%" }),
  b("release", { shot: "release", phone: "p-release", pills: ["releaseP1", "releaseP2", "releaseP3"], focus: "20% 30%" }),
  b("credits", { shot: "release-credits", pills: ["creditsP1", "creditsP2"], focus: "30% 50%" }),
  b("track", { shot: "track", phone: "p-track", pills: ["trackP1", "trackP2", "trackP3"], focus: "60% 40%" }),
  b("lyrics", { shot: "track-lyrics", pills: ["lyricsP1", "lyricsP2"], focus: "30% 50%" }),
  b("artist", { shot: "artist", phone: "p-artist", pills: ["artistP1", "artistP2", "artistP3"], focus: "40% 30%" }),
  b("market", { shot: "market", phone: "p-market", pills: ["marketP1", "marketP2", "marketP3", "marketP4"], focus: "50% 40%" }),
  b("book", { shot: "book", pills: ["bookP1", "bookP2", "bookP3"], focus: "40% 40%" }),
  b("sync", { shot: "sync", pills: ["syncP1", "syncP2", "syncP3"], focus: "50% 40%" }),
  b("explore", { shot: ["videos", "news", "demos", "labels"], pills: ["exploreP1", "exploreP2", "exploreP3"] }),
  b("search", { shot: "search", pills: ["searchP1", "searchP2"], focus: "50% 30%" }),
  b("academy", { shot: "academy", pills: ["academyP1", "academyP2", "academyP3"], focus: "40% 40%" }),
  b("free", { shot: ["tuner", "epx"], pills: ["freeP1", "freeP2"] }),
  b("studio", { mock: "rec", pills: ["studioP1", "studioP2", "studioP3", "studioP4", "studioP5"] }),
  b("dj", { mock: "decks", pills: ["djP1", "djP2", "djP3"] }),
  b("film", { mock: "timeline", pills: ["filmP1", "filmP2", "filmP3"] }),
  b("artwork", { mock: "layers", pills: ["artworkP1", "artworkP2"] }),
  b("master", { mock: "meter", lines: "meter", pills: ["masterP1", "masterP2"] }),
  b("splits", { mock: "donut", pills: ["splitsP1", "splitsP2", "splitsP3"] }),
  b("copi", { mock: "chat", lines: "chat", pills: ["copiP1", "copiP2", "copiP3"] }),
  b("business", { mock: "doc", pills: ["businessP1", "businessP2", "businessP3"] }),
  b("why", { shot: "why-calc", pills: ["whyP1", "whyP2"], focus: "50% 50%" }),
  b("open", { shot: ["platform", "angels"], pills: ["openP1", "openP2"] }),
  { ms: 8000, h: "join", body: "joinBody", pills: ["joinP1", "joinP2", "joinP3"], mock: "qr", lines: "joinUrl", cover: true },
];

/* ------------------------------------------------------------------ */
/*  The pulse: slow, fast, fast, slow, slow, fast                       */
/* ------------------------------------------------------------------ */

/** One tempo: 120 BPM. Fast cuts land on the beat — never more than three a
 *  second (WCAG 2.3.1); reduced motion stretches every cut to a second. */
const BEAT = 500;
const FASTER = 375;

export type PulseKind = "slow" | "fast" | "title";
export interface PulseBeat {
  ms: number;
  kind: PulseKind;
  /** `tourFilms.pulse.<w>` — a line (slow) or one word (fast). */
  w?: string;
  shot: ShotId;
  /** Crop for a fast cut: where to look and how close (1 = whole page). */
  at?: string;
  zoom?: number;
  outline?: boolean;
}

const fast = (ms: number, list: [string | undefined, ShotId, string, number, boolean?][]): PulseBeat[] =>
  list.map(([w, shot, at, zoom, outline]) => ({ ms, kind: "fast", w, shot, at, zoom, outline }));
const slow = (w: string, shot: ShotId, ms = BEAT * 8): PulseBeat => ({ ms, kind: "slow", w, shot });

export const PULSE: PulseBeat[] = [
  // 1 · slow — the room
  slow("s1", "home-intro"),
  slow("s2", "player"),
  slow("s3", "p-crate"),
  // 2 · fast — making it
  ...fast(BEAT, [
    ["dig", "crate", "50% 45%", 1.5],
    ["flip", "p-crate", "50% 40%", 1.2, true],
    ["play", "player", "50% 85%", 1.6],
    [undefined, "home", "30% 30%", 1.8],
    ["write", "track-lyrics", "30% 50%", 1.6, true],
    ["record", "track", "60% 35%", 1.7],
    ["mix", "release", "20% 30%", 1.5, true],
    [undefined, "artist", "40% 25%", 1.9],
    ["master", "release-credits", "40% 50%", 1.6],
    ["release", "release", "70% 40%", 1.4, true],
    [undefined, "p-release", "50% 20%", 1.2],
    ["share", "p-track", "50% 60%", 1.3, true],
  ]),
  // 3 · fast — getting paid, faster
  ...fast(BEAT, [
    ["sell", "market", "40% 40%", 1.6],
    ["book", "book", "40% 35%", 1.6, true],
    ["license", "sync", "50% 40%", 1.5],
    ["split", "release-credits", "30% 40%", 1.8, true],
  ]),
  ...fast(FASTER, [
    ["credit", "release-credits", "60% 60%", 2],
    ["own", "artist", "30% 30%", 1.7, true],
    [undefined, "p-market", "50% 40%", 1.3],
    ["paid", "why-calc", "50% 50%", 1.6, true],
    [undefined, "market", "70% 60%", 1.9],
    ["yours", "p-artist", "50% 30%", 1.4],
  ]),
  // 4 · slow — the names
  slow("s4", "release-credits"),
  slow("s5", "artist"),
  // 5 · slow — the deal
  slow("s6", "why-calc"),
  slow("s7", "platform"),
  // 6 · fast — everything at once, then the name
  ...fast(BEAT, [
    ["studio", "home-more", "50% 50%", 1.5],
    ["dj", "p-crate", "50% 50%", 1.3, true],
    ["film", "videos", "40% 40%", 1.6],
    ["art", "home", "60% 40%", 1.8, true],
    ["learn", "academy", "40% 40%", 1.6],
    ["tune", "tuner", "50% 50%", 1.4, true],
    ["beats", "epx", "50% 50%", 1.4],
    ["ask", "search", "50% 30%", 1.6, true],
  ]),
  ...fast(FASTER, [
    ["dig", "crate", "50% 50%", 1.3, true],
    ["play", "p-player", "50% 80%", 1.3],
    ["sell", "p-market", "50% 30%", 1.3, true],
    ["own", "p-home", "50% 50%", 1.2],
    ["together", "menu", "80% 30%", 1.5, true],
    [undefined, "home-intro", "50% 50%", 1.2],
  ]),
  { ms: BEAT * 10, kind: "title", w: "end", shot: "home-intro" },
];

/** Every picture a film needs, so the fast cuts never wait for one. */
export function shotsOf(film: "tour" | "pulse"): ShotId[] {
  const ids = film === "tour"
    ? TOUR.flatMap(x => [...(Array.isArray(x.shot) ? x.shot : x.shot ? [x.shot] : []), ...(x.phone ? [x.phone] : [])])
    : PULSE.map(x => x.shot);
  return [...new Set(ids)];
}
